import { ChevronLeft, ChevronRight, Loader2, Lock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

// The plain SignCare mark. NOT logo-black.png — that one reads "SignCare Studio",
// which is the name of the admin product the tenant uses, not something an applicant
// should ever be shown in their own verification journey.
import signCareLogo from '@/assets/brand/signcare-logo.svg';
import {
  SuccessScreen,
  type WelcomePreviewStep,
} from '@/features/workflows/components/simple/preview-screens';
import { cn } from '@/lib/utils';
import { useSessionQuestionnaireNames } from '../api/useSessionQuestionnaireNames';
import { usePublicRun } from '../publicRun';
import type { SessionDetailDto, SessionStepDto } from '../schemas/sessionSchemas';
import { formatSessionDateTime } from '../sessionDateTime';
import { ComingSoonStep } from '../steps/ComingSoonStep';
import { resolveStepModule } from '../steps/registry';
import { SessionExpiredScreen } from '../steps/SessionExpiredScreen';
import { SessionProgressBar } from './SessionProgressBar';
import { WelcomeIntroBlock } from './WelcomeIntro';
import { toWelcomeSteps, wasIntroSeen } from './welcomeSteps';

export type SessionPanelMode = 'fill' | 'view' | 'review';

const isStepDone = (s: SessionStepDto) => s.status === 'Passed' || s.status === 'Review';

// A step is locked when Auto-lock fired (Max Attempts exhausted) and the Lock
// Duration hasn't elapsed. Applicant submissions are blocked until then.
const isStepLocked = (s: SessionStepDto) =>
  s.lockedUntil != null && new Date(s.lockedUntil).getTime() > Date.now();

export function SessionMobilePanel({
  session,
  stepIndex,
  onStepChange,
  mode = 'fill',
}: {
  session: SessionDetailDto;
  /** Controlled step index driven by the sidebar (view/review). Omit for self-managed. */
  stepIndex?: number;
  onStepChange?: (i: number) => void;
  /**
   * `fill` — live applicant run: walks the steps in order, shows the designer
   * input screens with visible priority fallback, and the green success screen
   * only once every step is complete.
   * `view`/`review` — read-only detail for inspectors/reviewers (PanFormContent).
   */
  mode?: SessionPanelMode;
}) {
  const steps = session.steps.slice().sort((a, b) => a.position - b.position);

  if (mode === 'fill') {
    return <SessionFillFlow session={session} steps={steps} />;
  }

  return (
    <SessionReadOnlyPanel
      session={session}
      steps={steps}
      stepIndex={stepIndex}
      onStepChange={onStepChange}
    />
  );
}

// ── Fill (live applicant run) ────────────────────────────────────────────────
function SessionFillFlow({
  session,
  steps,
}: {
  session: SessionDetailDto;
  steps: SessionStepDto[];
}) {
  // Walk steps in order; the current step is the first not-yet-completed one.
  const firstPendingIdx = steps.findIndex((s) => !isStepDone(s));
  const allDone = firstPendingIdx === -1 && steps.length > 0;
  const current = firstPendingIdx >= 0 ? steps[firstPendingIdx] : undefined;
  const currentModule = current ? resolveStepModule(current) : null;

  // Progress bar (Workflow Settings → Options). Absent options → off, matching a
  // workflow that has never had the box ticked.
  const showProgress = session.options?.showProgressBar ?? false;
  const completedCount = steps.filter(isStepDone).length;

  // Welcome overview — the run's FIRST screen, listing what's ahead before the
  // applicant starts. Dismissed by Continue (local state: re-showing it after a
  // refresh costs nothing and loses no data). Skipped for a session that's already
  // part-way through, so a resumed applicant isn't sent back to the intro.
  // Starts dismissed when the applicant already saw the overview before the OTP gate
  // (the shared-link flow shows it first). Re-showing the identical screen after
  // verifying reads as the flow having restarted.
  const [introDismissed, setIntroDismissed] = useState(() => wasIntroSeen(session.publicToken));
  const showIntro =
    !session.isExpired && !allDone && !introDismissed && completedCount === 0 && steps.length > 0;

  // Shared with the pre-OTP overview, so both screens list the run identically.
  /**
   * Questionnaire names for the overview, resolved from the questionnaires rather than read off the
   * frozen step. See `toWelcomeSteps`'s `names` parameter for why the stored name is not enough.
   *
   * Non-blocking: the overview renders immediately with whatever names it has and fills the rest in
   * when they arrive, so the applicant's first screen is never waiting on this. The requests share
   * their cache with the questionnaire step's own fetch, so reaching that step later costs nothing.
   */
  const questionnaireNames = useSessionQuestionnaireNames(session.id, steps);
  const introSteps = useMemo<WelcomePreviewStep[]>(
    () => toWelcomeSteps(steps, questionnaireNames),
    [steps, questionnaireNames],
  );

  // Completion redirect (workflow Callback URL) — ONLY in the anonymous applicant
  // run (never when an admin fills internally). Build the target with the outcome
  // appended so the destination page knows the result.
  const publicRun = usePublicRun();
  const redirectTarget = useMemo(() => {
    const raw = session.completionRedirectUrl;
    if (!publicRun || !raw) return null;
    try {
      const u = new URL(raw);
      u.searchParams.set('status', session.status);
      if (session.referenceId) u.searchParams.set('ref', session.referenceId);
      return u.toString();
    } catch {
      return raw; // already validated http(s) server-side; use as-is if not parseable
    }
  }, [publicRun, session.completionRedirectUrl, session.status, session.referenceId]);

  // Once the run is complete, send the applicant back to the client's site after a
  // short pause (so they see the success screen first). A manual link is offered too.
  useEffect(() => {
    if (!allDone || !redirectTarget) return;
    const t = window.setTimeout(() => window.location.assign(redirectTarget), 2500);
    return () => window.clearTimeout(t);
  }, [allDone, redirectTarget]);

  // Tenant branding logo, CENTERED at the top of the card — same placement as
  // the Customization preview (mobile + desktop). SignCare logo when unset.
  // The tenant's uploaded logo (Customization → Logo) wins; the plain SignCare mark
  // is the fallback. `||` rather than `??` on purpose: the resolver hands back a
  // presigned URL, and a blank string is a real possibility when the key is missing
  // or presigning fails. `??` would let "" through and render a broken image, which
  // looks worse than no custom branding at all.
  const brandLogo = session.customization?.logoUrl || signCareLogo;

  return (
    // The three `sc-run-*` classes are inert on their own — they carry no styles
    // outside `.sc-session-preview-page`. They exist so the public run's scoped CSS
    // can reach these elements without touching the Tailwind that styles this panel
    // inside the authenticated shell (SessionFillScreen / SessionReviewScreen),
    // where the card-on-a-tinted-page look is the correct one.
    <div className="sc-run-shell flex h-full w-full flex-col overflow-y-auto bg-[#F1F5FB] px-4 py-8 md:px-8">
      <div className="sc-run-column mx-auto w-full max-w-lg">
        {/* Card fills the screen height so every step — form or the final
            success screen — has the same large footprint (not a short box).
            On a phone the scoped CSS drops the forced height and the frame, and
            caps it at the viewport. */}
        {/* Three stacked regions, which is the designer's own structure
            (WorkflowMobilePreview: progress → __content → __footer):

              1. the progress line   — fixed at the top, never scrolls
              2. `.sc-run-body`      — the ONLY scrolling region on a phone
              3. "Secured by"        — fixed at the bottom, never scrolls

            The scroll used to live on the card itself, which meant both the progress line
            and the footer scrolled along with the form. */}
        <div className="sc-run-card flex min-h-[calc(100dvh-8rem)] flex-col rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
          {/* Brand logo ONLY on the welcome overview.
              Once the run is under way each step carries its own title ("PAN
              Verification", "Aadhaar Verification"), so repeating the logo above it
              just pushed that title down and cost ~52px of the little vertical room
              a phone has. This matches the designer's step screens, which show the
              title and nothing above it. Also skipped on the expired notice, which
              is a terminal message rather than a branded step. */}
          {showIntro && !session.isExpired && (
            <div className="mb-3 flex h-9 w-full shrink-0 items-center justify-center">
              <img
                src={brandLogo}
                alt="Brand"
                className="max-h-full max-w-[150px] object-contain"
              />
            </div>
          )}
          {/* Progress bar — opt-in per workflow (Settings → Options). Hidden on the
              expired screen (a terminal notice, not a run step) and on the welcome
              overview, where no step has started yet. */}
          {showProgress && !session.isExpired && !showIntro && (
            <SessionProgressBar
              total={steps.length}
              completed={completedCount}
              isComplete={allDone}
            />
          )}
          {/* The scrolling region. `flex flex-1 flex-col` so a step that wants to fill the
              card (success screen, locked notice) still can, and so a step's own `mt-auto`
              button keeps landing at the bottom.

              No `min-h-0` here: that would let this shrink below its content on DESKTOP,
              where nothing scrolls inside the card and the page itself is the scroller — the
              content would simply spill out of the card. The phone rules add it, because
              there this must shrink to the viewport for `overflow-y` to have anything to
              do. */}
          <div className="sc-run-body flex flex-1 flex-col">
            {session.isExpired ? (
              <SessionExpiredScreen />
            ) : showIntro ? (
              // First screen of the run: what's ahead, then Continue into step 1.
              // The SAME block the shared link shows before the OTP gate.
              <WelcomeIntroBlock
                steps={introSteps}
                heading={session.options?.welcomeHeading ?? undefined}
                subheading={session.options?.welcomeSubheading ?? undefined}
                onContinue={() => setIntroDismissed(true)}
              />
            ) : allDone ? (
              // Final screen — shown only when every workflow step is complete,
              // centered in the same full-height card as the form steps.
              <div className="flex flex-1 flex-col items-center justify-center gap-4">
                <SuccessScreen />
                {redirectTarget && (
                  <p className="text-center text-[length:var(--text-14)] text-[#666666]">
                    Redirecting you back…{' '}
                    <a href={redirectTarget} className="font-medium text-primary underline">
                      Continue now
                    </a>
                  </p>
                )}
              </div>
            ) : current && isStepLocked(current) ? (
              // Attempt limit exhausted → applicant is locked out until lockedUntil.
              <LockedScreen step={current} />
            ) : current ? (
              currentModule ? (
                // key by step id so per-step state resets as the flow advances.
                <currentModule.Runner key={current.id} session={session} step={current} />
              ) : (
                <ComingSoonStep step={current} />
              )
            ) : (
              <p className="text-center text-sm text-[#666666]">No steps to complete.</p>
            )}
          </div>

          {/* "Secured by SignCare" — on EVERY screen of the run, as the designer's
              preview shows it. `mt-auto` pins it to the bottom of the card rather
              than letting it float directly under short content.

              Always the SignCare mark, never the tenant's: it states who secures the
              verification, so substituting the customer's own logo would make the
              line say something untrue. */}
          <div className="mt-auto flex shrink-0 items-center justify-center gap-1.5 pt-4 text-[length:var(--text-10)] text-primary">
            <span>Secured by</span>
            <img
              src={signCareLogo}
              alt="SignCare"
              className="h-3 w-auto max-w-[84px] shrink-0 object-contain"
            />
          </div>
        </div>

        {/* The per-step progress dots that used to sit under the card are gone.
            They belong to the BUILDER's preview, where they are a control for
            flipping between screens; in a real run the applicant cannot navigate
            with them, so they only ever announced "there are 4 steps and you are on
            2" — the workflow's own progress bar (Settings → Options) says that
            properly when a tenant wants it shown. Removing them also gives the card
            back the ~44px they cost, which matters on a phone. */}
      </div>
    </div>
  );
}

// Shown when the current step is locked (Max Attempts exhausted). The applicant
// cannot submit again until the Lock Duration elapses (lockedUntil).
function LockedScreen({ step }: { step: SessionStepDto }) {
  // Same formatter as every admin-facing timestamp, so an applicant is told "14 Aug 2026,
  // 12:21 pm" rather than a 24-hour time whose format depended on their browser locale.
  const unlockText = step.lockedUntil ? formatSessionDateTime(step.lockedUntil, '') : '';

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 py-10 text-center">
      <div className="mb-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#FEF2F2]">
        <Lock size={52} strokeWidth={2} className="text-[#DC2626]" />
      </div>
      <h2 className="mb-2 text-[length:var(--text-20)] font-semibold leading-tight text-[#111827]">
        Verification locked
      </h2>
      <p className="max-w-sm text-[length:var(--text-14)] leading-relaxed text-[#666666]">
        You&apos;ve reached the maximum number of verification attempts for this step
        {step.attemptCount > 0 ? ` (${step.attemptCount})` : ''}.
        {unlockText ? ` Please try again after ${unlockText}.` : ' Please try again later.'}
      </p>
    </div>
  );
}

// ── View / Review (read-only detail) ─────────────────────────────────────────
function SessionReadOnlyPanel({
  session,
  steps,
  stepIndex,
  onStepChange,
}: {
  session: SessionDetailDto;
  steps: SessionStepDto[];
  stepIndex?: number;
  onStepChange?: (i: number) => void;
}) {
  const [internalIdx, setInternalIdx] = useState(stepIndex ?? 0);
  const stepIdx = stepIndex !== undefined ? stepIndex : internalIdx;
  const setStepIdx = (i: number) => {
    setInternalIdx(i);
    onStepChange?.(i);
  };
  const [isPending, setIsPending] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const step = steps[stepIdx];
  if (!step) return null;

  const stepModule = resolveStepModule(step);
  const formId = `step-form-${step.id}`;
  const showVerifyButton =
    stepModule != null &&
    step.status !== 'Passed' &&
    step.status !== 'Review' &&
    step.status !== 'Failed';

  return (
    <div className="flex h-full w-full flex-col overflow-y-auto bg-[#F1F5FB] px-4 py-8 md:px-8">
      <div className="mx-auto w-full max-w-lg">
        <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm md:p-8">
          {stepModule ? (
            <stepModule.Detail
              session={session}
              step={step}
              formId={formId}
              onPendingChange={setIsPending}
              onReadyChange={setIsReady}
            />
          ) : (
            <ComingSoonStep step={step} />
          )}
        </div>

        {showVerifyButton && (
          <div className="mt-4">
            <button
              form={formId}
              type="submit"
              disabled={isPending || (!isReady && step.status === 'Pending')}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-[15px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isPending && <Loader2 size={15} className="animate-spin" />}
              Verify PAN
            </button>
          </div>
        )}

        {steps.length > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
              disabled={stepIdx === 0}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#111827] shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft size={18} strokeWidth={2.5} />
            </button>
            {steps.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStepIdx(i)}
                className={cn(
                  'rounded-full transition-all',
                  stepIdx === i ? 'h-2 w-5 bg-[#111827]' : 'h-2 w-2 bg-[#D1D5DB]',
                )}
              />
            ))}
            <button
              type="button"
              onClick={() => setStepIdx(Math.min(steps.length - 1, stepIdx + 1))}
              disabled={stepIdx === steps.length - 1}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#111827] shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronRight size={18} strokeWidth={2.5} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
